import { type ReactNode, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import ErrorState from '@/components/ErrorState';
import PageSkeleton from '@/components/PageSkeleton';
import type { DomainState, Theme } from '@escala/domain/types';
import Login, {
  CompleteProfile,
  Invitation,
  PublicRegistration,
  PublicVisitor,
  SetPassword,
  type ProfileSource,
  type RegistrationSource,
  type VisitorFormSource,
} from '@/pages/Login';
import { api } from '@/services/api/escala';
import { publicApi } from '@/services/api/public';
import {
  acceptProfileLink,
  currentActiveOrg,
  logout,
  onSessionChange,
  refresh,
  setActiveOrg,
} from '@/services/api/session';
import { ApiError } from '@/services/http';
import { AppStoreProvider, type ApiConnection } from '@/store/AppStore';
import { fromStateDto } from '@/store/remote';
import { useTheme } from '@/theme';

import { authPaths } from './paths';

type Phase =
  | { kind: 'loading' }
  | { kind: 'signedOut' }
  | { kind: 'error' }
  | { kind: 'signedIn'; state: DomainState; connection: ApiConnection; theme: Theme | null };

type TokenLink =
  | { kind: 'invite' | 'reset'; token: string }
  | { kind: 'register' | 'profile' | 'visitor'; slug: string; token: string };

/** O formulário público de cadastro: `/{igreja}/cadastro/{token}`. */
const REGISTER = /^\/([a-z0-9-]{3,40})\/cadastro\/([A-Za-z0-9_-]{1,64})\/?$/;

/** A ficha de visitante pelo link ou QR da igreja: `/{igreja}/visitante/{token}`. */
const VISITOR = /^\/([a-z0-9-]{3,40})\/visitante\/([A-Za-z0-9_-]{1,64})\/?$/;

/** O link pessoal para completar o cadastro: `/{igreja}/completar/{token}`. */
const COMPLETE = /^\/([a-z0-9-]{3,40})\/completar\/([A-Za-z0-9_-]{1,64})\/?$/;

/**
 * Link de fora da sessão: os do e-mail (`/convite?token=…`, `/redefinir-senha?token=…`) e o
 * do cadastro público (`/{igreja}/cadastro/{token}`), se a página veio de um.
 */
function tokenLink(): TokenLink | null {
  const { pathname, search } = window.location;
  const register = REGISTER.exec(pathname);

  if (register) return { kind: 'register', slug: register[1]!, token: register[2]! };

  const complete = COMPLETE.exec(pathname);
  const visitor = VISITOR.exec(pathname);

  if (visitor) return { kind: 'visitor', slug: visitor[1]!, token: visitor[2]! };

  if (complete) return { kind: 'profile', slug: complete[1]!, token: complete[2]! };

  const token = new URLSearchParams(search).get('token');

  if (!token) return null;
  if (pathname.endsWith(authPaths.invite)) return { kind: 'invite', token };
  if (pathname.endsWith(authPaths.reset)) return { kind: 'reset', token };

  return null;
}

/** Renova a sessão pelo cookie e carrega o estado: a fase seguinte da porta de entrada. */
async function loadSession(): Promise<Phase> {
  try {
    const session = await refresh();

    if (!session) return { kind: 'signedOut' };

    const [dto, me] = await Promise.all([api.state(), api.me()]);
    const { state, versions } = fromStateDto(dto);

    return {
      kind: 'signedIn',
      state,
      theme: me.theme,
      connection: {
        session: me.session,
        email: me.email,
        name: me.name,
        org: me.org,
        privacy: me.privacy,
        versions,
        client: api,
        reload: async () => fromStateDto(await api.state()),
        logout,
        // Trocada pela SessionGate (recarrega tudo na outra organização).
        switchOrg: () => {},
      },
    };
  } catch (error) {
    // A organização escolhida pela plataforma sumiu (ou o acesso mudou): volta à do acesso.
    if (currentActiveOrg() && error instanceof ApiError && (error.status === 403 || error.status === 404)) {
      setActiveOrg(null);

      return loadSession();
    }

    return { kind: 'error' };
  }
}

/**
 * Porta de entrada do modo API: renova a sessão pelo cookie (ou mostra o login), carrega
 * o estado (`GET /state`) e só então monta a store e as rotas. Sessão encerrada em
 * qualquer momento (refresh recusado, "Sair") volta para o login.
 */
export default function SessionGate({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [link, setLink] = useState(tokenLink);
  const { connect } = useTheme();
  const registration = useMemo<RegistrationSource | null>(
    () =>
      link?.kind === 'register'
        ? {
            load: () => publicApi.registrationForm(link.slug, link.token),
            submit: async (data, photo) => {
              await publicApi.submitRegistration(link.slug, link.token, data, photo);
            },
          }
        : null,
    [link]
  );
  const visitorForm = useMemo<VisitorFormSource | null>(
    () =>
      link?.kind === 'visitor'
        ? {
            load: () => publicApi.visitorForm(link.slug, link.token),
            submit: async (body) => {
              await publicApi.submitVisitor(link.slug, link.token, body);
            },
          }
        : null,
    [link]
  );
  const profile = useMemo<ProfileSource | null>(
    () =>
      link?.kind === 'profile'
        ? {
            load: () => publicApi.profileForm(link.token),
            save: async (data) => {
              await publicApi.saveProfile(link.token, data);
            },
            createAccount: async (email, password, termsVersion) => {
              await acceptProfileLink(link.token, email, password, termsVersion);
            },
          }
        : null,
    [link]
  );

  // Com a sessão aberta, o tema é o da conta (o mesmo em qualquer computador) e cada
  // troca é salva nela. Antes da pintura, para a aplicação não abrir no tema do login.
  const signedTheme = phase.kind === 'signedIn' ? phase.theme : undefined;
  useLayoutEffect(() => {
    if (signedTheme === undefined) return;

    return connect(signedTheme, (theme) => api.savePreferences({ theme }));
  }, [signedTheme, connect]);

  useEffect(() => {
    // O link do e-mail vem antes: define a senha e só depois abre a sessão.
    if (link) return;

    let active = true;

    void loadSession().then((next) => active && setPhase(next));

    return () => {
      active = false;
    };
  }, [link]);

  useEffect(() => onSessionChange((session) => !session && setPhase({ kind: 'signedOut' })), []);

  const reboot = () => {
    setPhase({ kind: 'loading' });
    void loadSession().then(setPhase);
  };

  /** A plataforma abre outra organização: a sessão inteira recarrega nela. */
  const switchOrg = (orgId: string) => {
    setActiveOrg(orgId);
    window.history.replaceState(null, '', '/');
    reboot();
  };

  /** Sai da tela do link: com a sessão aberta pelo convite, carrega a aplicação; senão, o login. */
  const leaveLink = (signedIn: boolean) => {
    window.history.replaceState(null, '', '/');
    setLink(null);
    if (!signedIn) setPhase({ kind: 'signedOut' });
  };

  if (registration) {
    return (
      <PublicRegistration
        source={registration}
        closeLabel={phase.kind === 'signedIn' ? 'Voltar ao Live Gospel' : 'Ir para o login'}
        onClose={() => leaveLink(phase.kind === 'signedIn')}
      />
    );
  }
  if (visitorForm) {
    return (
      <PublicVisitor
        source={visitorForm}
        closeLabel={phase.kind === 'signedIn' ? 'Voltar ao Live Gospel' : 'Ir para o login'}
        onClose={() => leaveLink(phase.kind === 'signedIn')}
      />
    );
  }
  if (profile) {
    return (
      <CompleteProfile
        source={profile}
        closeLabel={phase.kind === 'signedIn' ? 'Voltar ao Live Gospel' : 'Ir para o login'}
        onClose={() => leaveLink(phase.kind === 'signedIn')}
        onSignedIn={() => leaveLink(true)}
      />
    );
  }
  if (link?.kind === 'invite') return <Invitation token={link.token} onDone={leaveLink} />;
  if (link) return <SetPassword token={link.token} onDone={() => leaveLink(false)} />;

  switch (phase.kind) {
    case 'loading':
      return (
        <div className="min-h-dvh bg-page px-4 py-6">
          <PageSkeleton />
        </div>
      );
    case 'error':
      return <ErrorState onRetry={reboot} />;
    case 'signedOut':
      return (
        <Login
          onSignedIn={reboot}
          onRegister={(slug, token) => {
            window.history.pushState(null, '', `/${slug}/cadastro/${token}`);
            setLink({ kind: 'register', slug, token });
          }}
        />
      );
    case 'signedIn':
      return (
        <AppStoreProvider
          key={`${phase.connection.session.memberId}:${phase.connection.org.id}`}
          initialState={phase.state}
          api={{ ...phase.connection, switchOrg }}
        >
          {children}
        </AppStoreProvider>
      );
  }
}
